/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { CustomTextProjectStore } from 'src/customText/core/stores/CustomTextProjectStore';
import { customTextTeachingBubbleMessages } from 'src/customText/core/utils/customTextTeachingBubbleMessages';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

export type CustomTextProjectsCommandBarProps = TableCommandBarProps<CustomTextProjectStore> & {
    onCopyProject: () => void;
    onExportProject: () => void;
    onImportProject: () => void;
    onDeleteProjects: () => boolean | void;
    onCreateProject?: () => void;
    areExtractionProjects?: boolean;
    isCreateProjectModalHidden: boolean;
};

const messages = defineMessages({
    createNewProject: {
        id: 'customText.customTextProjectsCommandBar.createNewProject',
        defaultMessage: 'Create new project'
    },
    searchPlaceholder: {
        id: 'customText.customTextProjectsCommandBar.searchPlaceholder',
        defaultMessage: 'Search'
    },
    copyProject: {
        id: 'customText.customTextProjectsCommandBar.copyProject',
        defaultMessage: 'Copy project'
    },
    delete: {
        id: 'customText.customTextProjectsCommandBar.delete',
        defaultMessage: 'Delete'
    },
    export: {
        id: 'customText.customTextProjectsCommandBar.export',
        defaultMessage: 'Export'
    },
    import: {
        id: 'customText.customTextProjectsCommandBar.import',
        defaultMessage: 'Import'
    }
});

/**
 * Represents a command bar for the custom text projects table.
 */
export const CustomTextProjectsCommandBar = observer((props: CustomTextProjectsCommandBarProps) => {
    const {
        query = '',
        onCopyProject,
        onQueryChange,
        onCreateProject,
        onExportProject,
        onImportProject,
        onDeleteProjects,
        selectedItems = [],
        isCreateProjectModalHidden,
        areExtractionProjects = false
    } = props;

    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();
    const createProjectItemIdRef = React.useRef<string>(getId());

    const { isSignedIn } = rootStore.authStore;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canDeleteProject } = mapUserRoleToUserActions(userRole);

    React.useEffect(() => () => dequeueTeachingBubble('CustomTextCreateProject'), []);

    React.useEffect(() => {
        if (isSignedIn && isCreateProjectModalHidden && !uiStore.teachingBubbles.CustomTextCreateProject) {
            createTeachingBubble(
                'CustomTextCreateProject',
                localize(customTextTeachingBubbleMessages.createNewProjectBody, {
                    projectsType: areExtractionProjects ? 'extraction' : 'classification'
                }),
                localize(customTextTeachingBubbleMessages.createNewProjectTitle),
                `#${createProjectItemIdRef.current}`,
                true
            );
        } else {
            dequeueTeachingBubble('CustomTextCreateProject');
        }
    }, [isCreateProjectModalHidden, Object.values(uiStore.teachingBubbles)]);

    const commands: ICommandBarItemProps[] = [
        {
            key: 'import',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="importCTProjectButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onImportProject}
                            text={localize(messages.import)}
                            name={localize(messages.import)}
                            iconProps={{ iconName: 'Upload' }}
                            id={createProjectItemIdRef.current}
                            data-automation-id="createCTProjectButton"
                            disabled={!isSignedIn || !canWrite || item.disabled}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        { key: 'export', role: 'button', name: localize(messages.export), iconProps: { iconName: 'Download' }, onClick: onExportProject },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canDeleteProject || item.disabled}
                    data-automation-id="deleteCTProjectButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onDeleteProjects}
                            text={localize(messages.delete)}
                            name={localize(messages.delete)}
                            iconProps={{ iconName: 'Delete' }}
                            data-automation-id="deleteCTProjectButton"
                            disabled={!canDeleteProject || item.disabled}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ];

    if (onCreateProject) {
        const createProject: ICommandBarItemProps[] = [
            {
                key: 'create',
                onRender: (item: ICommandBarItemProps) => (
                    <RBACTooltip
                        hidden={canWrite || item.disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="createCTProjectButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onCreateProject}
                                iconProps={{ iconName: 'Add' }}
                                id={createProjectItemIdRef.current}
                                data-automation-id="createCTProjectButton"
                                text={localize(messages.createNewProject)}
                                name={localize(messages.createNewProject)}
                                disabled={!isSignedIn || !canWrite || item.disabled}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                )
            }
        ];

        commands.splice(0, 0, ...createProject);
    }

    if (FeatureToggles.CopyProject) {
        const copyProject: ICommandBarItemProps[] = [
            {
                key: 'copy',
                onRender: (item: ICommandBarItemProps) => (
                    <RBACTooltip
                        hidden={item.disabled || canWrite}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="copyCTProjectButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onCopyProject}
                                iconProps={{ iconName: 'Copy' }}
                                disabled={!canWrite || item.disabled || !isSignedIn}
                                text={localize(messages.copyProject)}
                                data-automation-id="copyCTProjectButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                )
            }
        ];

        commands.splice(3, 0, ...copyProject);
    }

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['create', 'import'],
        ['delete', 'export', 'copy'],
        ['delete'],
        selectedItems.length
    );

    return <LuisCommandBar items={items} includeSearchBar searchBarAlwaysExpanded query={query} onQueryChange={onQueryChange} />;
});
